import { cn } from "@d12labs/utils";
import type { ReactNode } from "react";
import { fmtActive, fmtActiveHours, fmtCount, fmtUsd, harnessLabel } from "../aggregate";
import { CLAUDE_OTHER, harnessColor, type Overview } from "../overview";

const RANGE_TEXT: Record<Overview["range"], string> = {
	"7d": "last 7 days",
	"30d": "last 30 days",
	"90d": "last 90 days",
	all: "all time",
};

export function HarnessDot({ harness }: { harness: string }) {
	return (
		<span
			aria-hidden
			className="size-2 flex-none rounded-full"
			style={{ background: harnessColor(harness) }}
		/>
	);
}

/** `computed` = priced by us at list rates (solid). `reported` = the harness's own figure (dashed). */
function SourceTag({ basis }: { basis: string }) {
	const computed = basis === "computed";
	return (
		<span
			title={computed ? "Priced by us at list rates" : "Reported by the harness"}
			className={cn(
				"rounded border px-1 py-px text-[10px] font-medium uppercase leading-4 tracking-[0.06em]",
				computed
					? "border-hairline-strong text-ink-subtle"
					: "border-dashed border-hairline-strong text-ink-tertiary",
			)}
		>
			{computed ? "computed" : "reported"}
		</span>
	);
}

function Eyebrow({ children }: { children: ReactNode }) {
	return <div className="text-eyebrow uppercase text-ink-tertiary">{children}</div>;
}

/**
 * Harness labels for the caption, split by cost basis. The `claude-other` row
 * folds into Claude Code so the caption names real harnesses, each once.
 */
export function basisLabels(harnesses: { harness: string; basis: string }[]): {
	computed: string[];
	reported: string[];
} {
	const labels = (rows: typeof harnesses) => [
		...new Set(rows.map((h) => harnessLabel(h.harness === CLAUDE_OTHER ? "claude" : h.harness))),
	];
	return {
		computed: labels(harnesses.filter((h) => h.basis === "computed")),
		reported: labels(harnesses.filter((h) => h.basis !== "computed")),
	};
}

/** Total + stacked harness split. Replaces the per-harness cards and the caveat banner. */
export function SpendHero({ overview }: { overview: Overview }) {
	const { total, harnesses, mixed } = overview;
	const { computed, reported } = basisLabels(harnesses);

	return (
		<section
			aria-label="Total spend"
			className="rounded-lg border border-hairline bg-surface-1 p-5"
		>
			<div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
				<div>
					<Eyebrow>Total spend · {RANGE_TEXT[overview.range]}</Eyebrow>
					<div className="mt-1 flex items-baseline gap-2">
						<span className="text-[40px] font-semibold leading-none tracking-[-0.025em] tabular-nums text-foreground">
							{fmtUsd(total)}
						</span>
						{mixed ? <span className="text-body-sm text-ink-subtle">≈ rough</span> : null}
					</div>
				</div>
				<p className="max-w-[420px] text-pretty text-right text-meta leading-snug text-ink-subtle">
					{mixed ? (
						<>
							Mixes <b className="font-medium text-ink-muted">computed</b> spend (
							{computed.join(", ")}, priced by us at list rates) with{" "}
							<b className="font-medium text-ink-muted">reported</b> spend ({reported.join(", ")},
							from the harness). Compare harnesses individually; treat the total as approximate.
						</>
					) : computed.length ? (
						<>
							All <b className="font-medium text-ink-muted">computed</b>: priced by us at list
							rates.
						</>
					) : (
						<>
							All <b className="font-medium text-ink-muted">reported</b> by the harness.
						</>
					)}
				</p>
			</div>

			<div
				role="img"
				aria-label={harnesses
					.map((h) => `${harnessLabel(h.harness)} ${fmtUsd(h.cost)} ${(h.share * 100).toFixed(1)}%`)
					.join(", ")}
				className="mt-4 flex h-2.5 gap-px overflow-hidden rounded-full bg-surface-3"
			>
				{harnesses.map((h) => (
					<span
						key={h.harness}
						title={`${harnessLabel(h.harness)} · ${fmtUsd(h.cost)}`}
						style={{
							width: `${Math.max(h.share * 100, 0.4)}%`,
							background: harnessColor(h.harness),
						}}
					/>
				))}
			</div>

			<div
				className="mt-4 grid divide-x divide-hairline"
				style={{ gridTemplateColumns: `repeat(${Math.max(harnesses.length, 1)}, minmax(0, 1fr))` }}
			>
				{harnesses.map((h, i) => (
					<div key={h.harness} className={cn("min-w-0", i === 0 ? "pr-4" : "px-4")}>
						<div className="flex min-w-0 items-center gap-2">
							<HarnessDot harness={h.harness} />
							<span className="truncate text-body-sm font-medium text-foreground">
								{harnessLabel(h.harness)}
							</span>
							<SourceTag basis={h.basis} />
						</div>
						<div className="mt-1.5 text-[22px] font-semibold tracking-[-0.015em] tabular-nums text-foreground">
							{fmtUsd(h.cost)}
						</div>
						<div className="text-meta tabular-nums text-ink-subtle">
							{h.sessions.toLocaleString("en-US")} sessions · {(h.share * 100).toFixed(1)}% of spend
							· {fmtActiveHours(h.activeMin)} active
						</div>
					</div>
				))}
			</div>
		</section>
	);
}

function monthName(monthStart: string): string {
	return new Date(`${monthStart}T00:00:00Z`).toLocaleDateString("en-US", {
		month: "long",
		year: "numeric",
		timeZone: "UTC",
	});
}

export function dayLabel(value: string | null | undefined): string {
	if (!value) return "";
	const date = value.length === 10 ? new Date(`${value}T00:00:00Z`) : new Date(value);
	return date.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

/** Context stats, one tier below the hero. */
export function StatStrip({ overview }: { overview: Overview }) {
	const { top, savings } = overview;
	const cells: { label: string; value: string; sub: string; tone?: string }[] = [
		{
			label: "This month",
			value: fmtUsd(overview.monthCost),
			sub:
				overview.monthFrom > overview.monthStart
					? `${dayLabel(overview.monthFrom)} – ${dayLabel(overview.anchor)} · range`
					: monthName(overview.monthStart),
		},
		{
			label: "Cost per prompt",
			value: overview.prompts ? fmtUsd(overview.costPerPrompt) : "—",
			sub: `across ${fmtCount(overview.prompts)} prompts`,
		},
		{
			label: "Active time",
			value: fmtActive(overview.activeMin),
			sub: overview.costPerActiveHour ? `${fmtUsd(overview.costPerActiveHour)} / active hour` : "—",
		},
		overview.hasClaude
			? {
					label: "Cache savings",
					value: fmtUsd(savings.saved),
					sub: `${(savings.readShare * 100).toFixed(0)}% of input served from cache · Claude Code`,
					tone: "text-semantic-success",
				}
			: { label: "Cache savings", value: "—", sub: "No Claude Code sessions in view" },
		top
			? {
					label: "Top session",
					value: fmtUsd(top.cost),
					sub: [top.title ?? "(untitled)", top.repo, dayLabel(top.first)].join(" · "),
				}
			: { label: "Top session", value: "—", sub: "No sessions in view" },
	];

	return (
		<section
			aria-label="Secondary stats"
			className="grid grid-cols-2 divide-hairline overflow-hidden rounded-lg border border-hairline bg-surface-1 lg:grid-cols-5 lg:divide-x"
		>
			{cells.map((cell) => (
				<div key={cell.label} className="min-w-0 px-4 py-3 last:col-span-2 lg:last:col-span-1">
					<Eyebrow>{cell.label}</Eyebrow>
					<div
						className={cn(
							"mt-1 text-[20px] font-semibold tracking-[-0.015em] tabular-nums",
							cell.tone ?? "text-foreground",
						)}
					>
						{cell.value}
					</div>
					<div className="truncate text-[11px] text-ink-subtle" title={cell.sub}>
						{cell.sub}
					</div>
				</div>
			))}
		</section>
	);
}
